import React, { useState, useEffect, useRef } from 'react'
import InternalTaskService from '../../../services/InternalTaskService'
import withReduxState from '../../../hoc/wReduxState'
import moment from 'moment'
import WithRoutify from '../../../hoc/WithRoutify'
import { Modal, Button } from 'react-bootstrap'
import Buttons from '../../../components/UI/Buttons'
import Icons from '../../../components/UI/Icons'
import VisitsService from '../../../services/VisitsService'
import Pagination from '../../../components/UI/TableGrid/Pagination'
import SortableTh from './SortableTh'


const MessagesTasks = (props) => {
  const { profile, Toast, Loader, navigate } = props
  const [tasks, setTasks] = useState([])
  const [filteredTasks, setFilteredTasks] = useState([])
  const [allFilteredTasks, setAllFilteredTasks] = useState([]) // Store all filtered tasks for pagination
  const [loading, setLoading] = useState(false)
  const [activeFilter, setActiveFilter] = useState('Open') // Open, Closed, All
  const [messageTypeFilter, setMessageTypeFilter] = useState('All') // Sent, Received, All
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [patientName, setPatientName] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [initialPosition, setInitialPosition] = useState(null)
  const containerRef = useRef(null)
  const originalPositionRef = useRef(null)
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0
  })
  const [sortColumn, setSortColumn] = useState('created_at')
  const [sortDirection, setSortDirection] = useState('desc')

  useEffect(() => {
    if (profile && profile.id) {
      fetchTasks()
    }
  }, [profile, isFullscreen])

  useEffect(() => {
    filterTasks()
  }, [tasks, activeFilter, messageTypeFilter, isFullscreen, sortColumn, sortDirection])

  useEffect(() => {
    if (isFullscreen && allFilteredTasks.length > 0) {
      applyPagination()
    }
  }, [pagination.page, pagination.perPage, allFilteredTasks, isFullscreen])

  const fetchTasks = async () => {
    setLoading(true)
    try {
      // Prepare payload with limit for initial load (5 records)
      const payload = {}
      if (!isFullscreen) {
        payload.limit = 5
      }
      
      // Fetch both received and sent tasks
      const [receivedData, sentData] = await Promise.all([
        InternalTaskService.getReceivedTasks(payload),
        InternalTaskService.getSentTasks(payload)
      ])
      
      const receivedTasks = Array.isArray(receivedData) ? receivedData.map(task => ({ ...task, isSent: false })) : []
      const sentTasks = Array.isArray(sentData) ? sentData.map(task => ({ ...task, isSent: true })) : []
      
      // Combine both lists
      let allTasks = [...receivedTasks, ...sentTasks]
      // Sort by created_at descending (newest first)
      allTasks.sort((a, b) => {
        const dateA = new Date(a.created_at)
        const dateB = new Date(b.created_at)
        return dateB - dateA
      })
      
      // Apply frontend limit if not fullscreen (in case backend doesn't support limit)
      if (!isFullscreen && allTasks.length > 5) {
        allTasks = allTasks.slice(0, 5)
      }
      
      setTasks(allTasks)
    } catch (error) {
      console.error('Error fetching tasks:', error)
      setTasks([])
    } finally {
      setLoading(false)
    }
  }

  const filterTasks = () => {
    let filtered = []
    
    // First filter by message type (Sent/Received/All)
    if (messageTypeFilter === 'Sent') {
      // Show only tasks sent by the current user
      filtered = tasks.filter(task => task.isSent === true)
    } else if (messageTypeFilter === 'Received') {
      // Show only tasks received by the current user
      filtered = tasks.filter(task => task.isSent === false)
    } else {
      // Show all tasks (both sent and received)
      filtered = tasks
    }
    
    // Then filter by status (Open/Closed/All)
    if (activeFilter === 'Open') {
      filtered = filtered.filter(task => task.status === 'pending')
    } else if (activeFilter === 'Closed') {
      filtered = filtered.filter(task => task.status === 'completed' || task.status === 'archived')
    }

    // Apply client-side sorting
    const sortCol = sortColumn
    const sortDir = sortDirection
    const allowedCols = ['created_at', 'from', 'to', 'message_body', 'task_title', 'status']
    if (allowedCols.includes(sortCol)) {
      filtered = [...filtered].sort((a, b) => {
        let aVal, bVal
        if (sortCol === 'created_at') {
          aVal = new Date(a.created_at || 0).getTime()
          bVal = new Date(b.created_at || 0).getTime()
          const result = aVal - bVal
          return sortDir === 'asc' ? result : -result
        }
        if (sortCol === 'from') {
          aVal = (getSenderName(a.sender) || '').toLowerCase()
          bVal = (getSenderName(b.sender) || '').toLowerCase()
        } else if (sortCol === 'to') {
          aVal = (getReceiverName(a.receiver) || '').toLowerCase()
          bVal = (getReceiverName(b.receiver) || '').toLowerCase()
        } else if (sortCol === 'message_body') {
          aVal = (a.message_body || '').toLowerCase()
          bVal = (b.message_body || '').toLowerCase()
        } else if (sortCol === 'task_title') {
          aVal = (a.task_title || '').toLowerCase()
          bVal = (b.task_title || '').toLowerCase()
        } else if (sortCol === 'status') {
          aVal = (getDisplayStatus(a.status) || '').toLowerCase()
          bVal = (getDisplayStatus(b.status) || '').toLowerCase()
        }
        const result = (aVal || '').localeCompare(bVal || '')
        return sortDir === 'asc' ? result : -result
      })
    }
    
    if (isFullscreen) {
      setAllFilteredTasks(filtered)
      setPagination(prev => ({ ...prev, totalRecords: filtered.length, page: 1 }))
    } else {
      setFilteredTasks(filtered)
    }
  }

  const applyPagination = () => {
    const start = (pagination.page - 1) * pagination.perPage
    const end = start + pagination.perPage
    const paginatedData = allFilteredTasks.slice(start, end)
    setFilteredTasks(paginatedData)
  }

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination)
  }

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      Loader(true)
      await InternalTaskService.updateTaskStatus(taskId, newStatus)
      Toast.success('Task status updated')
      fetchTasks()
      Loader(false)
    } catch (error) {
      console.error('Error updating task status:', error)
      Loader(false)
      Toast.error('Failed to update task status')
    }
  }

  const handleNavigate = (navigationLink) => {
    if (navigationLink) {
      navigate(navigationLink)
    }
  }

  const extractVisitIdFromUrl = (url) => {
    if (!url) return null
    // Extract visit_id from URLs like /visits/visit-details/123 or /visits/emr-details/123/visits
    const visitDetailsMatch = url.match(/\/visits\/(?:visit-details|emr-details)\/([^\/]+)/)
    if (visitDetailsMatch && visitDetailsMatch[1]) {
      return visitDetailsMatch[1]
    }
    return null
  }

  const handleViewTask = async (task) => {
    setSelectedTask(task)
    setViewModalOpen(true)
    setPatientName(null)
    
    // Fetch patient name if navigation link is a visit
    const visitId = extractVisitIdFromUrl(task.navigation_link)
    if (visitId) {
      try {
        Loader(true)
        const visitData = await VisitsService.getVisits(visitId)
        Loader(false)
        if (visitData && visitData[0] && visitData[0].employee) {
          const employee = visitData[0].employee
          const firstName = employee.first_name || ''
          const lastName = employee.last_name || ''
          const fullName = `${firstName} ${lastName}`.trim()
          if (fullName) {
            setPatientName(fullName)
          }
        }
      } catch (error) {
        Loader(false)
        console.error('Error fetching visit details:', error)
      }
    }
  }

  const handleModalNavigate = () => {
    if (selectedTask && selectedTask.navigation_link) {
      navigate(selectedTask.navigation_link)
      setViewModalOpen(false)
      setSelectedTask(null)
    }
  }

  const handleCloseModal = () => {
    setViewModalOpen(false)
    setSelectedTask(null)
    setPatientName(null)
  }

  const getDisplayStatus = (status) => {
    const statusMap = {
      'pending': 'Open',
      'completed': 'Closed',
      'archived': 'Closed',
    }
    return statusMap[status] || 'Open'
  }

  const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.4155 6.58674C10.1962 7.36741 10.1962 8.63474 9.4155 9.41674C8.63483 10.1974 7.3675 10.1974 6.5855 9.41674C5.80483 8.63608 5.80483 7.36874 6.5855 6.58674C7.3675 5.80474 8.63417 5.80474 9.4155 6.58674"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 7.99992C2 7.56059 2.10133 7.12592 2.29733 6.72525V6.72525C3.30733 4.66059 5.53933 3.33325 8 3.33325C10.4607 3.33325 12.6927 4.66059 13.7027 6.72525V6.72525C13.8987 7.12592 14 7.56059 14 7.99992C14 8.43925 13.8987 8.87392 13.7027 9.27458V9.27458C12.6927 11.3393 10.4607 12.6666 8 12.6666C5.53933 12.6666 3.30733 11.3393 2.29733 9.27458V9.27458C2.10133 8.87392 2 8.43925 2 7.99992Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  const ViewIcon = () => (
    <svg width="18" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.7683 8.59714C12.7442 9.57297 12.7442 11.1571 11.7683 12.1346C10.7925 13.1105 9.20834 13.1105 8.23084 12.1346C7.255 11.1588 7.255 9.57464 8.23084 8.59714C9.20834 7.61964 10.7917 7.61964 11.7683 8.59714"
        stroke="#1D2433"
        strokeOpacity="0.8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.5 10.3639C2.5 9.81468 2.62667 9.27135 2.87167 8.77052V8.77052C4.13417 6.18968 6.92417 4.53052 10 4.53052C13.0758 4.53052 15.8658 6.18968 17.1283 8.77052V8.77052C17.3733 9.27135 17.5 9.81468 17.5 10.3639C17.5 10.913 17.3733 11.4564 17.1283 11.9572V11.9572C15.8658 14.538 13.0758 16.1972 10 16.1972C6.92417 16.1972 4.13417 14.538 2.87167 11.9572V11.9572C2.62667 11.4564 2.5 10.913 2.5 10.3639Z"
        stroke="#1D2433"
        strokeOpacity="0.8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  const EditIcon = () => (
    <svg width="18" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.83203 6.66667V3.33333C5.83203 2.87333 6.20536 2.5 6.66536 2.5H13.332C13.792 2.5 14.1654 2.87333 14.1654 3.33333V6.66667"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.83333 14.1667H4.16667C3.24583 14.1667 2.5 13.4209 2.5 12.5001V8.33341C2.5 7.41258 3.24583 6.66675 4.16667 6.66675H15.8333C16.7542 6.66675 17.5 7.41258 17.5 8.33341V12.5001C17.5 13.4209 16.7542 14.1667 15.8333 14.1667H14.1667"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.83203 11.5H14.1654V16.6667C14.1654 17.1267 13.792 17.5 13.332 17.5H6.66536C6.20536 17.5 5.83203 17.1267 5.83203 16.6667V11.5Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M5.83203 9.16667H6.66536" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )

  const getBadgeClass = (status) => {
    const statusMap = {
      Open: 'sh_dashbg-red sh_dashtext-red',
      Incompleted: 'sh_dashbg-yellow sh_dashtext-yellow',
      Closed: 'sh_dashbg-green sh_dashtext-green',
    }
    return statusMap[status] || 'sh_dashbg-grey sh_dashtext-grey'
  }

  const handleFilterClick = (filter) => {
    setActiveFilter(filter)
  }

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(col)
      setSortDirection('asc')
    }
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleMessageTypeFilterClick = (filter) => {
    setMessageTypeFilter(filter)
  }

  const getActiveClass = (filter) => {
    return activeFilter === filter ? 'active' : ''
  }

  const getMessageTypeActiveClass = (filter) => {
    return messageTypeFilter === filter ? 'active' : ''
  }

  const getSenderName = (sender) => {
    if (!sender) return 'Unknown'
    const firstName = sender.first_name || ''
    const lastName = sender.last_name || ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || sender.email || 'Unknown'
  }

  const getReceiverName = (receiver) => {
    if (!receiver) return 'Unknown'
    const firstName = receiver.first_name || ''
    const lastName = receiver.last_name || ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || receiver.email || 'Unknown'
  }

  const getNavigationLabel = (navigationLink) => {
    if (!navigationLink) return 'View Related Record'
    
    // If we have patient name, show "Visit [Patient Name]"
    if (patientName && (navigationLink.includes('/visits/visit-details/') || navigationLink.includes('/visits/emr-details/'))) {
      return `Visit ${patientName}`
    }
    
    // Extract record type from navigation link
    if (navigationLink.includes('/visits/visit-details/') || navigationLink.includes('/visits/emr-details/')) {
      return 'Visit Record'
    } else if (navigationLink.includes('/employee/')) {
      return 'Employee Record'
    } else if (navigationLink.includes('/patients/')) {
      return 'Patient Record'
    } else if (navigationLink.includes('/appointments/')) {
      return 'Appointment Record'
    }
    
    return 'View Related Record'
  }

  return (
    <div 
      ref={containerRef}
      className={`${isFullscreen ? 'tblviewcard flip fullscreen-card' : 'col-lg-6 mb-2 pb-1'}`} 
      style={isFullscreen ? { 
        position: 'fixed', 
        top: initialPosition ? `${initialPosition.top}px` : '0', 
        left: initialPosition ? `${initialPosition.left}px` : '0', 
        transform: initialPosition ? 'none' : 'none',
        width: initialPosition ? `${initialPosition.width}px` : '100vw', 
        height: initialPosition ? `${initialPosition.height}px` : '100vh', 
        zIndex: 1040, 
        backgroundColor: '#fff', 
        padding: '20px', 
        borderRadius: initialPosition ? '8px' : '0', 
        boxShadow: initialPosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none', 
        transition: 'all 0.3s ease-in-out'
      } : { transition: 'all 0.3s ease-in-out' }}
    >
      <div className="card mb-0 border-0 shadow-none" style={isFullscreen ? { height: '100%', margin: 0 } : {}}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div className="d-flex align-items-center flex-wrap">
              <h6 className="text-md font-weight-bold pr-2 mb-0">Messages /Tasks</h6>
              
              {/* Message Type Filter Dropdown */}
              <div className="dropdown mr-2">
                <a 
                  className="btn btn-icon px-1 btn-transparent-dark dropdown-toggle position-static noArrow" 
                  data-bs-toggle="dropdown"
                  title="Filter by message type"
                  style={{ cursor: 'pointer' }}
                >
                  <Icons type="Filter" />
                </a>
                <div className="dropdown-menu dropdown-menu-start shadow">
                  <div 
                    className={`dropdown-item ${messageTypeFilter === 'All' ? 'active' : ''}`}
                    onClick={() => handleMessageTypeFilterClick('All')}
                  >
                    All
                  </div>
                  <div 
                    className={`dropdown-item ${messageTypeFilter === 'Sent' ? 'active' : ''}`}
                    onClick={() => handleMessageTypeFilterClick('Sent')}
                  >
                    Sent
                  </div>
                  <div 
                    className={`dropdown-item ${messageTypeFilter === 'Received' ? 'active' : ''}`}
                    onClick={() => handleMessageTypeFilterClick('Received')}
                  >
                    Received
                  </div>
                </div>
              </div>

              {/* Status Filter (Open/Closed/All) */}
              <ul className="nav sh_subnav border-0">
                <li>
                  <a 
                    href="#" 
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${getActiveClass('Open')}`}
                    aria-current="page"
                    onClick={(e) => {
                      e.preventDefault()
                      handleFilterClick('Open')
                    }}
                  >
                    Open
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${getActiveClass('Closed')}`}
                    onClick={(e) => {
                      e.preventDefault()
                      handleFilterClick('Closed')
                    }}
                  >
                    Closed
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${getActiveClass('All')}`}
                    onClick={(e) => {
                      e.preventDefault()
                      handleFilterClick('All')
                    }}
                  >
                    All
                  </a>
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center">
              <a 
                className="btn sh_btniconnew" 
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (!isFullscreen && containerRef.current) {
                    const rect = containerRef.current.getBoundingClientRect()
                    const pos = {
                      top: rect.top,
                      left: rect.left,
                      width: rect.width,
                      height: rect.height
                    }
                    originalPositionRef.current = pos
                    setInitialPosition(pos)
                    setIsFullscreen(true)
                    // Trigger transition to center after initial position is rendered
                    setTimeout(() => {
                      setInitialPosition(null)
                    }, 50)
                  } else {
                    // When closing, animate from center back to original position
                    if (originalPositionRef.current) {
                      setInitialPosition(originalPositionRef.current)
                      // Wait for animation to complete, then close
                      setTimeout(() => {
                        setIsFullscreen(false)
                        setInitialPosition(null)
                        originalPositionRef.current = null
                      }, 300) // Match transition duration
                    } else {
                      setIsFullscreen(false)
                      setInitialPosition(null)
                    }
                  }
                }}
                title={isFullscreen ? 'Minimize' : 'Maximize'}
              >
                {isFullscreen ? <CloseIcon /> : <SettingsIcon />}
              </a>
            </div>
          </div>
        </div>
        <div className={`card-body ${isFullscreen ? '' : 'sh_dashtableoverflowcard'} px-0 pt-0 mt-2 pb-2`} style={isFullscreen ? { height: 'calc(100% - 60px)', overflow: 'auto' } : {}}>
          <table className="table dataTable sh_tablenew nowrap table-hover" width="100%" cellSpacing="0" style={{ width: '100%' }}>
            <thead>
              <tr>
                <SortableTh label="SEND DATE" sortKey="created_at" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="FROM" sortKey="from" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="TO" sortKey="to" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="TYPE (MESSAGE)" sortKey="message_body" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="TASK" sortKey="task_title" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="STATUS" sortKey="status" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="ACTION" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredTasks.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-3 text-muted">
                    No tasks found
                  </td>
                </tr>
              ) : (
                filteredTasks.map((task, index) => {
                  const displayStatus = getDisplayStatus(task.status)
                  const sendDate = task.created_at ? moment(task.created_at).format('MM/DD/YYYY') : 'N/A'
                  const senderName = getSenderName(task.sender)
                  const receiverName = getReceiverName(task.receiver)
                  const isSentByCurrentUser = task.isSent || (task.sender && task.sender.id === profile?.id)
                  
                  return (
                    <tr key={task.id || index}>
                      <td className="forTable_textEllipsis">{sendDate}</td>
                      <td className="forTable_textEllipsis">{senderName}</td>
                      <td className="forTable_textEllipsis">{receiverName}</td>
                      <td className="forTable_textEllipsis">{task.message_body || 'N/A'}</td>
                      <td className="forTable_textEllipsis">{task.task_title || 'N/A'}</td>
                      <td className="forTable_textEllipsis">
                            <span className={`badge ${getBadgeClass(displayStatus)}`}>
                                  {displayStatus}
                            </span>
                      </td>
                      <td className="forTable_textEllipsis">
                        <a 
                          className="btn btn-icon px-1 btn-transparent-dark" 
                          title="View Details" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleViewTask(task)}
                        >
                          <ViewIcon />
                        </a>
                        {!isSentByCurrentUser && task.status === 'pending' && (
                          <a 
                            className="btn btn-icon px-1 btn-transparent-dark" 
                            title="Mark as Complete" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStatusUpdate(task.id, 'completed')}
                          >
                            <EditIcon />
                          </a>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
          {isFullscreen && allFilteredTasks.length > 0 && pagination.totalRecords > pagination.perPage && (
            <div className="px-3 pb-2">
              <Pagination
                pagination={pagination}
                fetchTableRecords={handlePaginationChange}
                tableRows={{ data: filteredTasks }}
              />
            </div>
          )}
        </div>
      </div>

      {/* View Task Modal - Gmail-like Design */}
      {viewModalOpen && selectedTask && (
        <Modal
          show={viewModalOpen}
          onHide={handleCloseModal}
          backdrop="static"
          keyboard={false}
          size="lg"
        >
          <div className="sh_modalBox">
            <Modal.Header closeButton>
              <Modal.Title>
                <h6 className="text-lg mb-0 font-weight-bold">
                  {selectedTask.task_title || 'Task Details'}
                </h6>
              </Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
              <div className="mb-3">
                <div className="d-flex align-items-center mb-3">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#023292',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      flexShrink: 0
                    }}
                  >
                    {getSenderName(selectedTask.sender).charAt(0).toUpperCase()}
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="font-weight-bold mr-2">
                      {getSenderName(selectedTask.sender)}
                    </span>
                    <span className="text-muted mr-2">•</span>
                    <span className="text-muted">
                      {selectedTask.created_at ? moment(selectedTask.created_at).format('MMM DD, YYYY [at] h:mm A') : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="border-bottom mb-3"></div>

                <div className="mb-3">
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTask.message_body || 'N/A'}
                  </div>
                </div>

                {selectedTask.navigation_link && (
                  <>
                  <div 
                    className="border rounded p-3"
                    onClick={handleModalNavigate}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: '#f8f9fa',
                      borderColor: '#023292 !important'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e7f1ff'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <Icons 
                          type="OpenNewTab" 
                          className="mr-2"
                          style={{ color: '#023292' }}
                        />
                        <span className="font-weight-bold" style={{ color: '#023292' }}>
                          {getNavigationLabel(selectedTask.navigation_link)}
                        </span>
                      </div>
                      <span style={{ color: '#023292' }}>→</span>
                    </div>
                  </div>
                   <div className="mt-2">
                    <p className="font-weight-bold mb-2">Status</p>
                    <style>
                      {`
                        .status-select-task option {
                          background-color: #ffffff !important;
                          color: #495057 !important;
                        }
                        .status-select-task option:hover,
                        .status-select-task option:checked {
                          background-color: #f8f9fa !important;
                          color: #495057 !important;
                        }
                      `}
                    </style>
                       <select
                           className="form-control form-control-sm sh_dashbg-green sh_dashtext-green status-select-task"
                               style={{
                               border: '1px solid #dee2e6',
                               borderRadius: '10px',
                               padding: '0.25rem 0.5rem',
                               fontSize: '0.85rem',
                               fontWeight: 500,
                               cursor: 'pointer',
                               width: '100%',
                               maxWidth: '100px',
                               backgroundColor: selectedTask.status === 'pending'
                               ? '#FEF1F2'
                               : selectedTask.status === 'completed'
                               ? '#E6F4EA'
                               :selectedTask.status === 'archived'
                               ? '#EFF3DA'
                               : '#e9ecef',
                               color: 
                               selectedTask.status === 'pending'
                               ? '#E02D3C'
                               : selectedTask.status === 'completed'
                               ? '#1E7E34'
                               :selectedTask.status === 'archived'
                               ? '#7D8F0D'
                               : '#495057',
                               }}
                               value={selectedTask.status}
                               onChange={(e) => {
                               handleStatusUpdate(selectedTask.id, e.target.value);
                               setSelectedTask({...selectedTask, status: e.target.value});
                               }}
                        >
                              <option value="pending">Open</option>
                              <option value="completed">Closed</option>
                              <option value="archived">Archived</option>
                     </select>
                  </div>
                  </>
                  
                )}
              </div>
            </Modal.Body>
            
            <Modal.Footer>
              <Button 
                variant="secondary" 
                onClick={handleCloseModal}
              >
                Close
              </Button>
            </Modal.Footer>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default withReduxState(WithRoutify(MessagesTasks))




